import React, { createContext, useContext, useState, useEffect } from 'react';

const LogoContext = createContext();

export const useLogo = () => {
  const context = useContext(LogoContext);
  if (!context) {
    throw new Error('useLogo must be used within a LogoProvider');
  }
  return context;
};

export const LogoProvider = ({ children }) => {
  const [logoPreview, setLogoPreview] = useState(() => {
    return localStorage.getItem('matrix-logo-preview') || null;
  });
  const [faviconPreview, setFaviconPreview] = useState(() => {
    return localStorage.getItem('matrix-favicon-preview') || null;
  });

  // Function to update the favicon in the document head
  const updateDocumentFavicon = (preview) => {
    // Remove existing favicon links
    const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
    existingFavicons.forEach(link => link.remove());

    // Create new favicon link
    const faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.type = 'image/x-icon';
    faviconLink.href = preview;
    
    // Add to document head
    document.head.appendChild(faviconLink);

    // Also update apple-touch-icon for better mobile support
    const appleTouchIcon = document.createElement('link');
    appleTouchIcon.rel = 'apple-touch-icon';
    appleTouchIcon.href = preview;
    document.head.appendChild(appleTouchIcon);
  };

  // Function to reset favicon to default
  const resetDocumentFavicon = () => {
    // Remove existing favicon links
    const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
    existingFavicons.forEach(link => link.remove());

    // Add default favicon
    const faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.href = '/favicon.ico';
    document.head.appendChild(faviconLink);

    // Add default apple-touch-icon
    const appleTouchIcon = document.createElement('link');
    appleTouchIcon.rel = 'apple-touch-icon';
    appleTouchIcon.href = '/logo192.png';
    document.head.appendChild(appleTouchIcon);
  };

  // Load saved data on mount
  useEffect(() => {
    const savedLogoPreview = localStorage.getItem('matrix-logo-preview');
    const savedFaviconPreview = localStorage.getItem('matrix-favicon-preview');
    
    if (savedLogoPreview) {
      setLogoPreview(savedLogoPreview);
    }
    if (savedFaviconPreview) {
      setFaviconPreview(savedFaviconPreview);
      // Apply the saved favicon to the document
      updateDocumentFavicon(savedFaviconPreview);
    }
  }, []);

  // Listen for storage changes (when settings page updates)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'matrix-logo-preview') {
        setLogoPreview(e.newValue);
      }
      if (e.key === 'matrix-favicon-preview') {
        setFaviconPreview(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const updateLogo = (preview) => {
    setLogoPreview(preview);
    if (preview) {
      localStorage.setItem('matrix-logo-preview', preview);
    } else {
      localStorage.removeItem('matrix-logo-preview');
    }
  };

  const updateFavicon = (preview) => {
    setFaviconPreview(preview);
    if (preview) {
      localStorage.setItem('matrix-favicon-preview', preview);
      // Update the actual favicon in the document head
      updateDocumentFavicon(preview);
    } else {
      localStorage.removeItem('matrix-favicon-preview');
      // Reset to default favicon
      resetDocumentFavicon();
    }
  };

  const value = {
    logoPreview,
    faviconPreview,
    updateLogo,
    updateFavicon,
  };

  return (
    <LogoContext.Provider value={value}>
      {children}
    </LogoContext.Provider>
  );
};
